/**
 * @file Generic Configuration Editor component
 * Provides editing interface for any config using useConfig
 * @version 1.0.0
 * @since 2025-10-10
 */

import { JSONEditor } from '../../json-editor/index.js';
import { getConfig } from '../composables/config.js';

export default {
  name: 'ConfigEditor',

  components: {
    JSONEditor,
  },

  props: {
    /**
     * Name of the config to edit (required)
     */
    configName: {
      type: String,
      required: true,
    },

    /**
     * Whether to display the root label
     */
    displayLabel: {
      type: Boolean,
      default: false,
    },

    /**
     * Editor mode
     */
    mode: {
      type: String,
      default: 'edit',
      validator: value =>
        ['edit', 'display', 'form', 'click-edit', 'field-click-edit'].includes(
          value
        ),
    },

    /**
     * Default options for editor components
     */
    defaults: {
      type: Object,
      default: () => ({ deletable: false }),
    },
  },

  inheritAttrs: false,

  setup(props) {},

  computed: {
    config() {
      return getConfig(this.configName);
    },
  },

  methods: {
    /**
     * Handle config updates from editor components
     * @param {object} state - Update state from editor component
     */
    async handleConfigUpdate(state) {
      if (state.state === 'updated' && state.update) {
        const updatedConfig = state.update(this.config.value);
        await this.config.replace(updatedConfig);
        await this.config.save();
      }
    },
  },

  template: `
    <div class="config-editor d-flex flex-column gap-2">
      <div v-if="config.error" class="alert alert-danger" role="alert">
        <strong>Error:</strong> {{ config.error.message }}
      </div>

      <div v-if="config.loading" class="d-flex justify-content-center my-3">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <JSONEditor
        v-if="!config.loading"
        :schema="config.schema"
        :value="config.value"
        :displayLabel="displayLabel"
        :mode="mode"
        :defaults="defaults"
        v-bind="$attrs"
        @input="handleConfigUpdate"
      />

      <div v-else-if="!config.schema && !config.loading" class="alert alert-info">
        No schema defined for config '{{ configName }}'. Raw value:
        <pre>{{ JSON.stringify(config.value, null, 2) }}</pre>
      </div>
    </div>
  `,
};
